import type { ReactNode } from 'react';

/**
 * "The Table" — the dark felt frame from the reference design `table-practice.html`
 * (feature 023, FR-029).
 *
 * Layout contract (desktop): a three-panel row — hand | auction | call — with an
 * optional full-width explain slot beneath it and a status line under the frame.
 *
 * The explain slot used to be mandatory and permanently reserved, so the grid could
 * never reflow mid-question (FR-031). It is optional now (client decision,
 * 2026-07-21): that slot is Jeff's commentary, most hands have none, and reserving
 * it meant an empty box on most questions. The reflow it was guarding against is
 * mild — the slot is BELOW all three panels, so its absence moves only the status
 * line, not the hand or the call pad the learner is reading.
 *
 * This component is presentation-only: it owns the frame, the grid and the panel
 * chrome, and knows nothing about problems, modules, rounds or answers. `ProblemsPage`
 * (all modes), `ModulePracticePage` and the Ask Jeff review surface each host their own
 * content in its slots (constitution I/II — open for extension, closed for
 * modification), and a future surface does the same rather than forking it.
 *
 * Every colour here comes from the felt tokens (`bg-felt`, `bg-felt-deep`, `border-brass`),
 * so a page changes the whole frame's surface by putting a theme class on an ancestor —
 * see the surface-theme block in index.css. Nothing in this file names a feature's colours.
 */

export interface TableFrameProps {
  /**
   * The masthead heading — names the module being practised. Rendered as the page's
   * h1, so a caller that omits it leaves the page without a top-level heading.
   */
  subtitle?: ReactNode;
  /** Masthead links on the right (e.g. "← Back to modules", "Leave the Table"). */
  mastheadLinks?: ReactNode;
  /**
   * A control at the end of the masthead's LEFT-hand group, directly after the subtitle
   * (client request, 2026-07-28). The practice pages put their ⓘ details trigger here so it
   * trails the info row it expands — last item on the left, reading as the "…and the rest"
   * of the position/id/score line rather than as one of the navigation controls opposite.
   *
   * It is a sibling of the h1, not part of `subtitle`, so an interactive control never lands
   * inside the heading and gets folded into its accessible name.
   */
  mastheadSubtitleAction?: ReactNode;
  /**
   * A static label trailing that action — the last thing in the left-hand group, after the
   * control rather than before it (feature 028: Counting practice's "Declarer: West", which the
   * pre-028 page kept in exactly that position and which its table's controls strip has to drop
   * on phones for want of width).
   *
   * Text, not a control: it rides in the group with the h1 and the ⓘ, so anything interactive
   * belongs in `mastheadSubtitleAction` instead. Optional, and nothing is rendered without it —
   * every caller that passes none is unchanged. Whatever is passed sits on the felt, so it has
   * to carry its own light colour, and its own visibility: this slot is width-agnostic, and a
   * caller that wants it on phones only says so on the node.
   */
  mastheadSubtitleTrailing?: ReactNode;
  /**
   * An optional row of page controls INSIDE the frame, between the masthead and the panel row
   * (feature 025, client decision 2026-07-27). The review surface puts its filter dropdowns here
   * and /problems its mid-session difficulty range, so a control belongs to the same container as
   * the panels it acts on and spans exactly their width, rather than floating above the frame on
   * the page background at some narrower width of its own.
   *
   * Whatever is passed sits on the felt, so its own text has to be light — and moving a page's
   * controls in here is what qualifies a route for the phone treatment in Layout.tsx, since page
   * chrome above the frame is what leaves a landscape phone's panel row nothing to occupy.
   *
   * Carries the `table-controls` hook so that landscape CSS can reclaim its padding.
   *
   * A row here is a control the panels can afford only where there is height to spare. On a
   * phone-treated route (TABLE_SESSION_ROUTES) the frame is locked to the viewport and must not
   * scroll, so a caller whose controls are optional should gate this row with `controlsClassName`
   * and offer the same control from the masthead — see /problems' difficulty trigger.
   */
  controls?: ReactNode;
  /**
   * Extra classes for the controls ROW (not its contents) — the caller's hook for gating it by
   * viewport, which also collapses the row's own 22/18px padding, as a `display:none` on the
   * contents alone would not. /problems passes `DIFFICULTY_RANGE_INLINE`: the bands whose height can
   * carry its difficulty range, with a masthead trigger covering the rest. Empty by default — a
   * caller that says nothing keeps the row at every width, as the review surface's filter bar does.
   */
  controlsClassName?: string;
  /** Left panel — the hand, plus whatever the page shows beneath it. */
  hand: ReactNode;
  /**
   * An optional extra panel inserted between the hand and the auction (feature 025): the review
   * surface shows partner's hand here, in its own container beside the reviewer's. Omitted on the
   * practice pages, which keep the three-panel row unchanged.
   */
  secondaryHand?: ReactNode;
  /**
   * Centre panel — the auction table.
   *
   * Optional since feature 028: Counting practice's hand panel is a whole play table that draws
   * its OWN auction beside the dummy, so a second auction panel here would render the same bids
   * twice. Every other caller passes one and is unaffected — when this is omitted the section is
   * not rendered at all, rather than rendered empty.
   */
  auction?: ReactNode;
  /** Right panel — the call pad / Yes-No pad. */
  call: ReactNode;
  /** Full-width explanation row. Omitted entirely when there is nothing to explain. */
  explain?: ReactNode;
  /** Status line beneath the frame (position, streak, difficulty, optional pin). */
  statusLine?: ReactNode;
  /**
   * An action pinned to the bottom-right of the frame, under the masthead links (feature 025,
   * client decision 2026-07-22). The masthead already carries a long module title and two
   * links, so an extra button there crowds the line; the foot of the frame is empty and puts
   * the control in the same column as "Leave the Table".
   *
   * The practice pages no longer use it for "Ask Jeff" (feature 033, 2026-10-15): on phones the
   * status line is hidden, so the button was alone in this row and the row's height clipped Skip
   * Hand, Next and Counting's playback strip. Ask Jeff now rides in the answer panel's button row.
   */
  footerAction?: ReactNode;
  /**
   * How the footer action lines up against the status line.
   *
   * `bottom` (default) keeps it level with the LAST status row, at the foot of the frame.
   *
   * `status-line` lifts it onto the FIRST status row, aligned on the baseline of
   * "ID: … · Problem N of M · …" (client decision 2026-07-27). A page whose footer holds the
   * primary navigation wants it there: bottom-aligned, a two-row status line (the long "From: …"
   * tournament title wraps onto its own row) pushes the buttons a row further down, where they
   * fall below the fold on a laptop.
   */
  footerActionAlign?: 'bottom' | 'status-line';
  /** Heading for the left panel. Defaults to "You hold". */
  handLabel?: ReactNode;
  /** Heading for the right panel. Defaults to "Your call". */
  callLabel?: ReactNode;
  /**
   * How the panel row is divided (feature 028).
   *
   * `three-panel` (default) is the hand | auction | call row every 023 surface has had: fixed
   * 270/300px sidebars on desktop, proportional columns on a portrait tablet.
   *
   * `wide-hand` is for a caller whose hand panel is not a hand but a whole table — Counting
   * practice puts a play table there: two hands, the played trick, the trick count, the
   * vulnerability board AND its own auction. At 270px that panel is clipped to the played cards
   * alone (measured 2026-08-31), so this hands it the row's leftover width and keeps only the call
   * column fixed. It also stacks a portrait tablet rather than splitting it three ways, which is
   * what the pre-028 Counting page did at that width.
   *
   * The row carries a `table-panels--wide-hand` hook in this mode, which is how index.css reaches
   * the bands where a grid template alone is not enough — the landscape phone row, and the stacked
   * phone column where the hand panel has to become the shrinker the absent auction panel was.
   */
  layout?: 'three-panel' | 'wide-hand';
  /**
   * Overrides the outer felt-background wrapper's classes (default `bg-felt-bg py-6`). A caller
   * that renders its own controls directly above the frame can trim the top padding here so the
   * frame sits closer to them, without changing the spacing on the practice pages.
   */
  className?: string;
}

/**
 * Small panel heading — `.panel h4` in the reference stylesheet.
 *
 * 12px at every viewport, and phones drop the capitals (constitution VI — text-size floor /
 * no all-caps on phones, amended 2026-07-29). It was 11px uppercase everywhere, which broke
 * both: this audience skews past 60, and small caps are read letter by letter because the
 * ascender profile that lets a word be recognised by shape is gone. The letter-spacing rides
 * with the capitals — where they are dropped it has nothing to space out.
 */
function PanelHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.09em] text-ink-soft narrow:normal-case narrow:tracking-normal padswap:mb-1.5">
      {children}
    </h2>
  );
}

// Small phones trade the frame's generous padding for content room (client request,
// 2026-07-27: every control in view, no scrolling); sm: restores the reference spacing.
// padswap (short single-column viewports) trims the vertical padding one step further —
// sized against iPhone SE (375x547), the shortest portrait phone in the e2e viewport
// matrix, where the frame overran its no-scroll budget by ~29px.
const PANEL =
  'rounded-[10px] bg-cream px-3 py-2.5 text-ink shadow-[0_2px_6px_rgba(0,0,0,.25)] sm:px-[18px] sm:py-4 padswap:py-2';

export function TableFrame({
  subtitle,
  mastheadLinks,
  mastheadSubtitleAction,
  mastheadSubtitleTrailing,
  controls,
  controlsClassName = '',
  hand,
  secondaryHand,
  auction,
  call,
  explain,
  statusLine,
  footerAction,
  footerActionAlign = 'bottom',
  handLabel = 'You hold',
  callLabel = 'Your call',
  layout = 'three-panel',
  className = 'bg-felt-bg py-6',
}: TableFrameProps) {
  // A secondary hand adds a fourth column between the hand and the auction; the explain row and the
  // grid template both have to account for it. Without it the three-panel row is exactly as before.
  //
  // A portrait tablet gets the three-panel row too (client request, 2026-07-29): it is only
  // the nav links that have to collapse at that width, not the layout. Its template is
  // proportional rather than the desktop's fixed 270/300 sidebars — at 768px those two would
  // leave the auction 118px, less than its four seat columns need. `auto` lets the hand hug
  // its cards (an East/West hand sets its own width beside the board; a North/South one stacks
  // and is much narrower) and hands the rest to the two panels that can use it.
  //
  // Only the three-panel case. The four-panel review surface keeps stacking here — a second
  // hand column at 768px leaves nothing worth reading.
  //
  // `wide-hand` (feature 028) is the third case: two columns, the hand taking whatever the call
  // pad does not need. No `tablet-pt:` arm — at 768px this hand panel is a play table that wants
  // the whole width, so the row simply stays stacked there.
  //
  // The three-panel row is keyed on `desk-row:` rather than `lg:` (feature 031). Its first arm
  // IS `lg`, so nothing changes from 1024 up; its second brings the row down to 951 wide for
  // landscape viewports from 501 tall (a zoomed 960x540 desktop, a 1000x620 window), which the
  // stacked column used to clip. The other two templates keep `lg:` on purpose: the Counting
  // table (FR-011) and the four-panel review surface are not in this feature's scope.
  const wideHand = layout === 'wide-hand';
  const gridCols = wideHand
    ? 'lg:grid-cols-[minmax(0,1fr)_280px]'
    : secondaryHand
      ? // `auto 1fr` rows: the call column spans both rows (below), and a spanning item's extra
        // height goes to the flexible track. So the hands and the auction keep their natural row
        // height and the explain row beneath them takes up the rest of the call column's length.
        'lg:grid-cols-[250px_170px_1fr_290px] lg:grid-rows-[auto_1fr]'
      : 'tablet-pt:grid-cols-[minmax(0,auto)_minmax(0,1fr)_minmax(0,1.15fr)] desk-row:grid-cols-[270px_1fr_300px]';
  // The four-panel review surface (the AI-predictions page, its only user) keeps the explain row
  // under the hands and the auction only, and lets the call column run down beside it (user,
  // 2026-10-09): a long predicted auction used to push the explain row below a strip of empty
  // panel space under the hands.
  const explainSpan = wideHand
    ? 'lg:col-span-2'
    : secondaryHand
      ? 'lg:col-span-3'
      : 'tablet-pt:col-span-3 desk-row:col-span-3';
  const callSpan = secondaryHand && !wideHand ? ' lg:row-span-2' : '';

  // ANSWERED-STATE ARRANGEMENT (client report, 2026-09-21).
  //
  // Jeff's commentary takes a full-width row BENEATH the three panels, so the panel area costs
  // `max(hand, auction, call) + gap + Jeff` — and on a short screen that is more height than
  // there is. The frame is `overflow-hidden` inside a viewport lock, so the overflow was not a
  // scrollbar but a silent clip: the client's screenshots show the status row cut off at
  // 1366x768 and the hand panel drawn with no cards at all at 832x372.
  //
  // This hook lets index.css move Jeff's box up BESIDE the hand — under the auction and answer
  // panels, with the hand column spanning both rows — in the height bands that were short. The
  // cost becomes `max(hand, auction + gap + Jeff)`, which is roughly Jeff's row back. It is a
  // class rather than a different `explainSpan` because the arrangement is height-gated on
  // MEASURED bands — the desktop row below 1000 tall, landscape phones below 600 — and a
  // Tailwind screen cannot express "this width band, but only at these heights" without adding
  // two more entries to a `screens` map that already carries fifteen.
  //
  // It has since become the general "the answered state is on screen" hook (client report,
  // 2026-09-22), which is why its name outgrew the placement it was added for. index.css keys
  // three further things on it, in EVERY band and not only the two above: the commentary box
  // fills the height it is given rather than sizing to its text, the auction's row is sized to
  // its content so the leftover reaches the commentary, and on the portrait column the
  // commentary is the panel that shrinks first. All of that must apply exactly when there IS a
  // commentary, which is what this boolean says — so the hook is right, and only the two bands
  // named above also change the PLACEMENT.
  //
  // Only the default three-panel layout. Counting's `wide-hand` table and the four-panel review
  // surface keep their own grids untouched (031 FR-011), and with no explain slot there is
  // nothing to move.
  const explainBeside = Boolean(explain) && !wideHand && !secondaryHand;

  return (
    // `data-testid="table-practice"` is a FROZEN legacy identifier: the shared e2e layout
    // battery (e2e/helpers/problem-layout.ts) and a dozen specs anchor on it across every
    // page that uses this frame. It names the frame, not the practice feature — renaming it
    // would be a pure test-churn cost with no reader benefit.
    <div className={`table-session-outer ${className}`} data-testid="table-practice">
      {/* `table-frame` and the `table-*` hooks below are the landscape-phone layout's handles:
          on a landscape phone the frame becomes a flex column whose panel row takes the leftover
          height, so the three panels sit side by side and scroll inside themselves rather than
          growing the page. See the landscape block in index.css — the classes are inert everywhere
          else. */}
      <div className="table-frame mx-auto max-w-[1080px] overflow-hidden rounded-[14px] bg-felt shadow-[0_12px_40px_rgba(0,0,0,.4)]">
        {(subtitle || mastheadLinks || mastheadSubtitleAction || mastheadSubtitleTrailing) && (
          // suit-on-dark: the heading's bid tokens render through SuitSymbol, whose black
          // clubs and spades are invisible against the felt. See index.css.
          //
          // Phones carry very little vertical padding here (client request, 2026-07-28, measured
          // against the pre-023 /problems strip): whatever the row's tallest control is, that is
          // the strip's height, and every pixel on top of it is margin taken off a table that
          // has to fit the viewport whole. sm: keeps the reference spacing, where the masthead
          // is a title line rather than a control strip.
          //
          // `py-1` is SYMMETRIC deliberately. It briefly compensated for the brass rule instead
          // (pt-0.5 pb-0), on the theory that a bottom-only border is painted inside the strip
          // and so lands the content that many pixels high. That is true geometrically and wrong
          // perceptually (client report, 2026-07-28): the eye reads the brass rule as the
          // separator BELOW the strip, not as strip surface, so what has to look centred is the
          // felt-deep band alone. The old asymmetry also left the filled brass exit tab with 0px
          // of felt beneath it, so it merged into the rule and read as sitting on top of it —
          // 4px above and below is what makes the whole row float clear.
          <div className="table-masthead suit-on-dark flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 border-b-2 border-brass bg-felt-deep px-2.5 py-1 sm:border-b-[3px] sm:gap-3 sm:px-6 sm:py-4">
            {/* The subtitle IS the page's h1. "Hawkes Bridge" was here too until
                2026-07-21, but the site masthead directly above already says it, and
                repeating the brand inside the frame pushed the one line that matters —
                which module the learner is in — down into small italic print. Dropping
                it also keeps a top-level heading on the page, which axe requires: the
                only other headings are the h2 panel labels. */}
            {(subtitle || mastheadSubtitleAction || mastheadSubtitleTrailing) && (
              // One flex child so `justify-between` treats the subtitle and its trailing control
              // as a single left-hand group, whatever the masthead links do on the right.
              <div className="flex min-w-0 items-center gap-1 sm:gap-2">
                {subtitle && (
                  <h1 className="min-w-0 font-serif text-xl font-bold text-cream">{subtitle}</h1>
                )}
                {mastheadSubtitleAction}
                {mastheadSubtitleTrailing}
              </div>
            )}
            {mastheadLinks && (
              // Phones tighten the gap: a caller that collapses these links to icon buttons
              // (module practice) pays 40px of pure gap here otherwise, which is the
              // difference between one masthead row and two. Desktop keeps the reference
              // spacing, where the links are words rather than icons.
              // `ml-auto` rather than leaving it to `justify-between`: when the title is long
              // enough to push these onto a second line, justify-between has only one item to
              // place there and lands it hard left, under the title (client report, 2026-07-29,
              // tablet portrait). An auto margin follows the links onto the wrapped line and
              // keeps them against the right edge, where they are on a single line too.
              <div className="ml-auto flex items-center gap-1 text-[13.5px] font-semibold sm:gap-5">
                {mastheadLinks}
              </div>
            )}
          </div>
        )}

        {/* Page controls, on the felt, above the panels they act on. `pb-0` deliberately: the grid
            below already carries p-[22px], so its own top padding is the gap. */}
        {controls && (
          <div className={`table-controls px-[22px] pt-[18px] ${controlsClassName}`}>{controls}</div>
        )}

        {/* Three panels on desktop (four with a secondary hand); US7 stacks them on phones/tablets.
            padswap tightens the stack's padding and gaps a step further: on short single-column
            viewports those pixels are the difference between the frame fitting the viewport and
            growing a scrollbar (client no-scroll contract, measured at 360x640). */}
        <div
          className={
            `table-panels${wideHand ? ' table-panels--wide-hand' : ''}` +
            `${explainBeside ? ' table-panels--explain-beside' : ''} grid grid-cols-1 gap-2.5 p-2.5 ` +
            `sm:gap-[18px] sm:p-[22px] padswap:gap-1.5 padswap:p-1.5 ${gridCols}`
          }
        >
          <section className={`${PANEL} table-panel-hand`} aria-label="Your hand">
            {/* Some callers pass `handLabel={null}` and render the label directly above the
                hand instead (so it rides with the seat-placed hand). Only draw the panel-top
                heading when a label is actually supplied. */}
            {handLabel && <PanelHeading>{handLabel}</PanelHeading>}
            {hand}
          </section>

          {secondaryHand && (
            <section className={PANEL} aria-label="Additional hand">
              {secondaryHand}
            </section>
          )}

          {/* Omitted entirely when the caller draws its own auction (feature 028 — Counting's play
              table carries one beside the dummy). An empty bordered box would read as a panel that
              failed to load. */}
          {auction && (
            <section className={`${PANEL} table-panel-auction`} aria-label="The auction">
              <PanelHeading>The Auction</PanelHeading>
              {auction}
            </section>
          )}

          <section className={`${PANEL} table-panel-call${callSpan}`} aria-label="Your call">
            <PanelHeading>{callLabel}</PanelHeading>
            {call}
          </section>

          {/* Full-width — spans every column on desktop, and the whole row beneath the three
              panels on a landscape phone (index.css sets grid-column: 1 / -1 there). The
              four-panel surface is the exception: it spans the hands and the auction only, with
              the call column beside it. */}
          {explain && <div className={`${explainSpan} table-panel-explain`}>{explain}</div>}
        </div>

        {(statusLine || footerAction) && (
          // The status line keeps the row's full width when there is no footer action, so a
          // page that passes none is laid out exactly as before.
          //
          // `items-baseline` for the `status-line` alignment rather than `items-start`: the action
          // is taller than a line of status text, so a shared top edge would still leave it hanging
          // below the labels. Matching baselines puts the button's own text level with "ID: …",
          // whatever either one's padding or font size is.
          <div
            // `table-status-row` is the CSS hook the height-starved bands trim this row by
            // (index.css) — on a short laptop its 20px of bottom padding is the difference
            // between the review pin being on screen and being under the fold.
            className={
              'table-status-row flex flex-wrap justify-between gap-3 px-2.5 pb-2 sm:px-[22px] sm:pb-5 padswap:pb-0.5 ' +
              (footerActionAlign === 'status-line' ? 'items-baseline' : 'items-end')
            }
          >
            {statusLine && <div className="min-w-0 flex-1">{statusLine}</div>}
            {footerAction && <div className="shrink-0">{footerAction}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
